import { Input } from '../core/Input';

const JOYSTICK_RADIUS = 60; // px
const JOYSTICK_DEADZONE = 0.32; // fraction of radius before a direction registers
const LOOK_SENSITIVITY = 1.3; // touch-drag pixels → mouse-delta pixels

interface ActionButton {
  readonly label: string;
  readonly onDown: () => void;
  readonly onUp: () => void;
}

/**
 * On-screen controls for touch devices. It translates a left-hand virtual
 * joystick, right-hand look-drag and a cluster of action buttons into the same
 * synthetic input the keyboard/mouse path produces — so the simulation is
 * completely agnostic about the input source.
 *
 * Each screen zone owns the touches that begin on it (touch events keep firing
 * on their original target), which keeps multi-touch tracking simple.
 */
export class TouchControls {
  private readonly root = document.createElement('div');
  private readonly joystickBase = document.createElement('div');
  private readonly joystickKnob = document.createElement('div');

  private moveTouchId: number | null = null;
  private readonly moveOrigin = { x: 0, y: 0 };
  private lookTouchId: number | null = null;
  private readonly lookLast = { x: 0, y: 0 };
  private active = false;

  constructor(private readonly input: Input) {
    this.root.id = 'touch';
    this.root.classList.add('hidden');

    const moveZone = this.createZone('touch-move');
    const lookZone = this.createZone('touch-look');
    this.setupJoystick(moveZone);
    this.setupLook(lookZone);

    this.joystickBase.className = 'joystick-base';
    this.joystickKnob.className = 'joystick-knob';
    this.joystickBase.appendChild(this.joystickKnob);
    this.joystickBase.style.display = 'none';

    this.root.append(moveZone, lookZone, this.joystickBase, this.createButtons());
    document.body.appendChild(this.root);

    // Show/hide together with the play session.
    this.input.onLockChange((locked) => this.setActive(locked));
  }

  private setActive(active: boolean): void {
    this.active = active;
    this.root.classList.toggle('hidden', !active);
    if (!active) this.releaseMovementKeys();
  }

  // --- joystick (movement) ---------------------------------------------

  private setupJoystick(zone: HTMLElement): void {
    zone.addEventListener(
      'touchstart',
      (e) => {
        if (!this.active || this.moveTouchId !== null) return;
        e.preventDefault();
        const t = e.changedTouches[0];
        this.moveTouchId = t.identifier;
        this.moveOrigin.x = t.clientX;
        this.moveOrigin.y = t.clientY;
        this.joystickBase.style.display = 'block';
        this.joystickBase.style.left = `${t.clientX}px`;
        this.joystickBase.style.top = `${t.clientY}px`;
        this.moveKnob(0, 0);
      },
      { passive: false },
    );
    zone.addEventListener(
      'touchmove',
      (e) => {
        const t = this.findTouch(e, this.moveTouchId);
        if (!t) return;
        e.preventDefault();
        this.updateJoystick(t.clientX - this.moveOrigin.x, t.clientY - this.moveOrigin.y);
      },
      { passive: false },
    );
    const end = (e: TouchEvent): void => {
      if (!this.touchEnded(e, this.moveTouchId)) return;
      this.moveTouchId = null;
      this.joystickBase.style.display = 'none';
      this.releaseMovementKeys();
    };
    zone.addEventListener('touchend', end);
    zone.addEventListener('touchcancel', end);
  }

  private updateJoystick(dx: number, dy: number): void {
    const dist = Math.hypot(dx, dy);
    const clamp = dist > JOYSTICK_RADIUS ? JOYSTICK_RADIUS / dist : 1;
    this.moveKnob(dx * clamp, dy * clamp);

    const nx = dx / JOYSTICK_RADIUS;
    const ny = dy / JOYSTICK_RADIUS;
    this.input.setKey('KeyW', ny < -JOYSTICK_DEADZONE);
    this.input.setKey('KeyS', ny > JOYSTICK_DEADZONE);
    this.input.setKey('KeyA', nx < -JOYSTICK_DEADZONE);
    this.input.setKey('KeyD', nx > JOYSTICK_DEADZONE);
  }

  private moveKnob(x: number, y: number): void {
    this.joystickKnob.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
  }

  private releaseMovementKeys(): void {
    for (const code of ['KeyW', 'KeyA', 'KeyS', 'KeyD']) this.input.setKey(code, false);
  }

  // --- look (camera) ----------------------------------------------------

  private setupLook(zone: HTMLElement): void {
    zone.addEventListener(
      'touchstart',
      (e) => {
        if (!this.active || this.lookTouchId !== null) return;
        e.preventDefault();
        const t = e.changedTouches[0];
        this.lookTouchId = t.identifier;
        this.lookLast.x = t.clientX;
        this.lookLast.y = t.clientY;
      },
      { passive: false },
    );
    zone.addEventListener(
      'touchmove',
      (e) => {
        const t = this.findTouch(e, this.lookTouchId);
        if (!t) return;
        e.preventDefault();
        this.input.addLookDelta(
          (t.clientX - this.lookLast.x) * LOOK_SENSITIVITY,
          (t.clientY - this.lookLast.y) * LOOK_SENSITIVITY,
        );
        this.lookLast.x = t.clientX;
        this.lookLast.y = t.clientY;
      },
      { passive: false },
    );
    const end = (e: TouchEvent): void => {
      if (this.touchEnded(e, this.lookTouchId)) this.lookTouchId = null;
    };
    zone.addEventListener('touchend', end);
    zone.addEventListener('touchcancel', end);
  }

  // --- action buttons ---------------------------------------------------

  private createButtons(): HTMLElement {
    const cluster = document.createElement('div');
    cluster.className = 'touch-buttons';
    const buttons: ActionButton[] = [
      { label: 'REEL', onDown: () => this.input.setKey('ShiftLeft', true), onUp: () => this.input.setKey('ShiftLeft', false) },
      { label: 'JUMP', onDown: () => this.input.setKey('Space', true), onUp: () => this.input.setKey('Space', false) },
      { label: '🕸️L', onDown: () => this.input.firePointerDown(0), onUp: () => this.input.firePointerUp(0) },
      { label: '🕸️R', onDown: () => this.input.firePointerDown(2), onUp: () => this.input.firePointerUp(2) },
    ];
    for (const def of buttons) cluster.appendChild(this.createButton(def));
    return cluster;
  }

  private createButton({ label, onDown, onUp }: ActionButton): HTMLElement {
    const btn = document.createElement('button');
    btn.className = 'touch-btn';
    btn.textContent = label;
    const down = (e: TouchEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      btn.classList.add('pressed');
      onDown();
    };
    const up = (e: TouchEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      btn.classList.remove('pressed');
      onUp();
    };
    btn.addEventListener('touchstart', down, { passive: false });
    btn.addEventListener('touchend', up);
    btn.addEventListener('touchcancel', up);
    return btn;
  }

  // --- helpers ----------------------------------------------------------

  private createZone(className: string): HTMLElement {
    const zone = document.createElement('div');
    zone.className = `touch-zone ${className}`;
    return zone;
  }

  private findTouch(e: TouchEvent, id: number | null): Touch | null {
    if (id === null) return null;
    for (const t of Array.from(e.touches)) if (t.identifier === id) return t;
    return null;
  }

  private touchEnded(e: TouchEvent, id: number | null): boolean {
    if (id === null) return false;
    for (const t of Array.from(e.changedTouches)) if (t.identifier === id) return true;
    return false;
  }
}
